import type { NoticeKind, NoticeOutcome, NoticeRow } from "@agency/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, Clock, MessageSquare, Phone, Plus, Sparkles, UserRound } from "lucide-react";
import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { callAction } from "../api";
import { useConfig } from "../components/ConfigProvider";
import { useToast } from "../components/Toast";
import { Badge, Button, Card, Checkbox, EmptyState, ErrorState, Input, LoadingState, Select, Sheet, Tabs, Textarea, cn } from "../components/ui";
import { useLocale, useT } from "../i18n";
import { money, shortDate } from "../theme";

/**
 * Carrier notices: the accountability loop. One row per thing a carrier told us about a policy, with the owner,
 * the first-touch clock, every contact attempt and the outcome. Faraday fills this from reports, emails and
 * photos; people work it here or from the customer file.
 */

type View = "open" | "mine" | "resolved" | "all";
const KINDS: NoticeKind[] = ["cancellation_pending", "cancelled", "nonpay", "reinstated", "renewal_offer", "premium_increase", "nonrenewal", "underwriting_inquiry", "missing_document", "payment_due", "claim_update", "other"];
const OUTCOMES: NoticeOutcome[] = ["saved", "paid", "reinstated", "rewritten", "lost", "not_needed", "other"];

export function NoticesScreen() {
  const t = useT();
  const [view, setView] = useState<View>("open");
  const [adding, setAdding] = useState(false);
  const status = view === "mine" ? "open" : view === "resolved" ? "resolved" : view;
  const q = useQuery({ queryKey: ["notices", view], queryFn: async () => (await callAction<"agency.notice.list", NoticeRow[]>("agency.notice.list", { status, mine: view === "mine", limit: 500 })).result });
  const rows = view === "resolved" ? (q.data ?? []) : q.data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">{t("notices.title")}</h1>
          <p className="text-ink/60">{t("notices.subtitle")}</p>
        </div>
        <Button onClick={() => setAdding(true)}><Plus className="h-4 w-4" />{t("notices.add")}</Button>
      </div>
      <Tabs<View> value={view} onChange={setView} tabs={[{ id: "open", label: t("notices.filter.open") }, { id: "mine", label: t("notices.filter.mine") }, { id: "resolved", label: t("notices.filter.resolved") }, { id: "all", label: t("notices.filter.all") }]} />
      {q.isLoading ? <LoadingState /> : null}
      {q.isError ? <ErrorState error={q.error} onRetry={() => q.refetch()} /> : null}
      {q.data && rows.length === 0 ? (
        <EmptyState title={t("notices.none")} action={<p className="flex items-center gap-2 text-sm text-ink/60"><Sparkles className="h-4 w-4 text-primary" />{t("notices.noneHint")}</p>} />
      ) : null}
      {rows.length ? <NoticeList rows={rows} /> : null}
      <AddNoticeSheet open={adding} onClose={() => setAdding(false)} />
    </div>
  );
}

export function NoticeList({ rows, compact = false }: { rows: NoticeRow[]; compact?: boolean }) {
  return (
    <div className="space-y-2">
      {rows.map((r) => <NoticeCard key={r.notice.id} row={r} compact={compact} />)}
    </div>
  );
}

function slaLabel(r: NoticeRow, t: (k: string) => string): { text: string; tone: "red" | "amber" | "green" | "neutral" } {
  const n = r.notice;
  if (n.status === "resolved" || n.status === "lost") return { text: t(`notices.status.${n.status}`), tone: n.status === "lost" ? "red" : "green" };
  if (n.firstTouchAt) return { text: `${t("notices.touched")} · ${t("notices.touches").replace("{n}", String(n.touches.length))}`, tone: "green" };
  const h = r.slaHoursLeft ?? 0;
  if (h < 0) return { text: t("notices.slaOverdue").replace("{h}", String(-h)), tone: "red" };
  return { text: t("notices.slaLeft").replace("{h}", String(h)), tone: h <= 4 ? "amber" : "neutral" };
}

export function NoticeCard({ row, compact }: { row: NoticeRow; compact?: boolean }) {
  const t = useT();
  const { locale } = useLocale();
  const { config } = useConfig();
  const { toast } = useToast();
  const qc = useQueryClient();
  const n = row.notice;
  const open = n.status === "new" || n.status === "assigned" || n.status === "contacted";
  const [panel, setPanel] = useState<"none" | "touch" | "close" | "assign">("none");
  const [channel, setChannel] = useState<"call" | "text" | "email" | "visit" | "note">("call");
  const [reached, setReached] = useState(false);
  const [note, setNote] = useState("");
  const [outcome, setOutcome] = useState<NoticeOutcome>("saved");
  const [assignee, setAssignee] = useState(n.assigneeUserId ?? "");
  const sla = slaLabel(row, t);
  const refresh = () => { for (const k of ["notices", "today", "customer"]) qc.invalidateQueries({ queryKey: [k] }); };
  const fail = (e: unknown) => toast({ title: t("common.error"), body: (e as Error).message, tone: "error" });

  const touch = useMutation({
    mutationFn: () => callAction("agency.notice.touch", { noticeId: n.id, channel, reached, note }),
    onSuccess: () => { toast({ title: t("notices.touchSaved"), tone: "success" }); setPanel("none"); setNote(""); setReached(false); refresh(); },
    onError: fail,
  });
  const close = useMutation({
    mutationFn: () => callAction("agency.notice.resolve", { noticeId: n.id, outcome, note: note || undefined }),
    onSuccess: () => { toast({ title: t("notices.closed"), tone: "success" }); setPanel("none"); setNote(""); refresh(); },
    onError: fail,
  });
  const assign = useMutation({
    mutationFn: () => callAction("agency.notice.assign", { noticeId: n.id, assigneeUserId: assignee }),
    onSuccess: () => { toast({ title: t("notices.assigned"), tone: "success" }); setPanel("none"); refresh(); },
    onError: fail,
  });
  const staff = config.users.filter((u) => u.role !== "executor");
  const urgent = row.daysToEffective != null && row.daysToEffective <= 3 && open;

  return (
    <Card className={cn("px-4 py-3", urgent && "border-red-200 bg-red-50/40")}>
      <div className="flex flex-wrap items-start gap-x-4 gap-y-1">
        <div className="min-w-0 flex-1">
          <p className="flex flex-wrap items-center gap-2">
            {n.customerId ? <Link to={`/customers/${n.customerId}`} className="font-bold hover:underline">{row.customerName}</Link> : <span className="font-bold">{row.customerName}</span>}
            {!n.customerId ? <Badge tone="amber">{t("notices.unmatched")}</Badge> : null}
            <Badge tone={n.kind === "cancellation_pending" || n.kind === "cancelled" || n.kind === "nonpay" || n.kind === "nonrenewal" ? "red" : n.kind === "renewal_offer" || n.kind === "premium_increase" ? "amber" : "blue"}>{t(`notices.kind.${n.kind}`)}</Badge>
            <span className="text-sm text-ink/60">{n.carrier}{n.policyNumber ? ` · ${n.policyNumber}` : ""}</span>
          </p>
          <p className="mt-0.5 text-xs text-ink/55">
            {t("notices.received")} {shortDate(n.receivedAt, locale)} · {t(`notices.source.${n.source}`)}
            {n.effectiveAt ? <> · {t("notices.effectiveAt")} <span className={cn("font-semibold", urgent && "text-red-700")}>{shortDate(n.effectiveAt.length === 10 ? `${n.effectiveAt}T12:00:00` : n.effectiveAt, locale)}{row.daysToEffective != null ? ` (${row.daysToEffective < 0 ? t("notices.effectivePast") : t("notices.daysToEffective").replace("{d}", String(row.daysToEffective))})` : ""}</span></> : null}
            {n.amountCents != null ? <> · {money(n.amountCents)}</> : null}
          </p>
          {n.details && !compact ? <p className="mt-1 text-sm text-ink/80">{n.details}</p> : null}
          {n.outcome ? <p className="mt-1 text-sm"><CheckCircle2 className="mr-1 inline h-4 w-4 text-primary-dark" />{t(`notices.outcome.${n.outcome}`)}{n.outcomeNote ? ` — ${n.outcomeNote}` : ""}</p> : null}
        </div>
        <div className="flex flex-col items-end gap-1 text-right">
          <Badge tone={sla.tone}>{sla.tone === "red" ? <AlertTriangle className="h-3 w-3" /> : <Clock className="h-3 w-3" />} {sla.text}</Badge>
          <button type="button" onClick={() => setPanel(panel === "assign" ? "none" : "assign")} className="inline-flex items-center gap-1 text-xs text-ink/60 hover:text-ink"><UserRound className="h-3.5 w-3.5" />{row.assigneeName}</button>
        </div>
      </div>
      {!compact && n.touches.length ? (
        <ul className="mt-2 space-y-0.5 border-l-2 border-ink/10 pl-3 text-xs text-ink/70">
          {n.touches.map((x, i) => <li key={i}>{shortDate(x.at, locale)} · {t(`notices.channel.${x.channel}`)}{x.reached ? ` · ${t("notices.reached")}` : ""}{x.note ? ` — ${x.note}` : ""} <span className="text-ink/45">({config.users.find((u) => u.id === x.userId)?.name.split(" ")[0] ?? x.userId})</span></li>)}
        </ul>
      ) : null}
      {open ? (
        <div className="mt-2 flex flex-wrap gap-2">
          <Button size="sm" variant={panel === "touch" ? "primary" : "outline"} onClick={() => setPanel(panel === "touch" ? "none" : "touch")}><Phone className="h-4 w-4" />{t("notices.logTouch")}</Button>
          <Button size="sm" variant={panel === "close" ? "primary" : "outline"} onClick={() => setPanel(panel === "close" ? "none" : "close")}><CheckCircle2 className="h-4 w-4" />{t("notices.close")}</Button>
        </div>
      ) : null}
      {panel === "touch" ? (
        <form className="mt-2 grid gap-2 rounded-xl bg-ink/[0.03] p-3 sm:grid-cols-[160px_1fr_auto]" onSubmit={(e: FormEvent) => { e.preventDefault(); touch.mutate(); }}>
          <Select value={channel} onChange={(e) => setChannel(e.target.value as typeof channel)} aria-label={t("notices.logTouch")}>
            {(["call", "text", "email", "visit", "note"] as const).map((c) => <option key={c} value={c}>{t(`notices.channel.${c}`)}</option>)}
          </Select>
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder={t("notices.touchNote")} aria-label={t("notices.touchNote")} />
          <div className="flex items-center gap-3"><Checkbox checked={reached} onChange={setReached} label={t("notices.reached")} /><Button type="submit" size="sm" loading={touch.isPending}>{t("common.save")}</Button></div>
        </form>
      ) : null}
      {panel === "close" ? (
        <form className="mt-2 grid gap-2 rounded-xl bg-ink/[0.03] p-3 sm:grid-cols-[180px_1fr_auto]" onSubmit={(e: FormEvent) => { e.preventDefault(); close.mutate(); }}>
          <Select value={outcome} onChange={(e) => setOutcome(e.target.value as NoticeOutcome)} aria-label={t("notices.outcome")}>
            {OUTCOMES.map((o) => <option key={o} value={o}>{t(`notices.outcome.${o}`)}</option>)}
          </Select>
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder={t("notices.touchNote")} aria-label={t("notices.touchNote")} />
          <Button type="submit" size="sm" loading={close.isPending}>{t("notices.close")}</Button>
        </form>
      ) : null}
      {panel === "assign" ? (
        <form className="mt-2 flex flex-wrap items-center gap-2 rounded-xl bg-ink/[0.03] p-3" onSubmit={(e: FormEvent) => { e.preventDefault(); if (assignee) assign.mutate(); }}>
          <Select value={assignee} onChange={(e) => setAssignee(e.target.value)} aria-label={t("notices.assignee")} className="sm:w-64">
            <option value="">{t("notices.unassigned")}</option>
            {staff.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </Select>
          <Button type="submit" size="sm" loading={assign.isPending} disabled={!assignee}>{t("notices.assign")}</Button>
        </form>
      ) : null}
    </Card>
  );
}

function AddNoticeSheet({ open, onClose, customerId }: { open: boolean; onClose: () => void; customerId?: string }) {
  const t = useT();
  const { config } = useConfig();
  const { toast } = useToast();
  const qc = useQueryClient();
  const [form, setForm] = useState({ carrier: "", kind: "cancellation_pending" as NoticeKind, policyNumber: "", customerName: "", effectiveAt: "", amount: "", details: "", assigneeUserId: "" });
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const create = useMutation({
    mutationFn: () => callAction<"agency.notice.create", NoticeRow & { duplicate: boolean }>("agency.notice.create", {
      carrier: form.carrier, kind: form.kind, policyNumber: form.policyNumber || undefined, customerName: form.customerName || undefined, customerId,
      effectiveAt: form.effectiveAt || undefined, amountCents: form.amount ? Math.round(Number(form.amount) * 100) : undefined, details: form.details, source: "manual", assigneeUserId: form.assigneeUserId || undefined,
    }),
    onSuccess: (r) => {
      toast({ title: r.result.duplicate ? t("notices.duplicate") : t("notices.recorded"), tone: r.result.duplicate ? "neutral" : "success" });
      for (const k of ["notices", "today", "customer"]) qc.invalidateQueries({ queryKey: [k] });
      onClose();
      setForm({ carrier: "", kind: "cancellation_pending", policyNumber: "", customerName: "", effectiveAt: "", amount: "", details: "", assigneeUserId: "" });
    },
    onError: (e) => toast({ title: t("common.error"), body: (e as Error).message, tone: "error" }),
  });
  const carriers = config.agency.carriers.map((c) => c.name);
  return (
    <Sheet open={open} onClose={onClose} title={t("notices.add")}>
      <form className="space-y-3" onSubmit={(e: FormEvent) => { e.preventDefault(); if (form.carrier.trim()) create.mutate(); }}>
        <Input label={t("notices.carrier")} list="notice-carriers" value={form.carrier} onChange={set("carrier")} required />
        <datalist id="notice-carriers">{carriers.map((c) => <option key={c} value={c} />)}</datalist>
        <Select label={t("notices.kind")} value={form.kind} onChange={set("kind")}>{KINDS.map((k) => <option key={k} value={k}>{t(`notices.kind.${k}`)}</option>)}</Select>
        <div className="grid gap-3 sm:grid-cols-2">
          <Input label={t("notices.policyNumber")} value={form.policyNumber} onChange={set("policyNumber")} />
          {!customerId ? <Input label={t("notices.customerName")} value={form.customerName} onChange={set("customerName")} /> : null}
          <Input label={t("notices.effectiveAt")} type="date" value={form.effectiveAt} onChange={set("effectiveAt")} />
          <Input label={t("notices.amount")} type="number" step="0.01" min="0" value={form.amount} onChange={set("amount")} />
        </div>
        <Textarea placeholder={t("notices.details")} value={form.details} onChange={set("details")} />
        <Select label={t("notices.assignee")} value={form.assigneeUserId} onChange={set("assigneeUserId")}>
          <option value="">{t("notices.unassigned")}</option>
          {config.users.filter((u) => u.role !== "executor").map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
        </Select>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>{t("common.cancel")}</Button>
          <Button type="submit" loading={create.isPending}><MessageSquare className="h-4 w-4" />{t("notices.add")}</Button>
        </div>
      </form>
    </Sheet>
  );
}

export { AddNoticeSheet };
